import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

// Import standardized form components used in Research and Image Generators
import { StandardTextField } from '@/components/ui/form-fields/StandardTextField';
import StandardTextAreaField from '@/components/ui/form-fields/StandardTextAreaField';
import { StandardSelect } from '@/components/ui/form-fields/StandardSelect';
import GradientButtonCentered from '@/components/ui/GradientButtonCentered';
import GlassPill from '@/components/ui/GlassPill';

// Import enhanced toggle component
import { SimpleAdvancedToggle } from '@/components/research-generator/ResearchAssistant/components/SimpleAdvancedToggle';
import WritingStyleDropdown from '@/components/content-generator/editor/WritingStyleDropdown';
import { pageTypeOptions, PageType } from '@/components/content-generator/editor/pages/new-page-smart-detection';

export interface PageCreationFormData {
  title: string;
  pageType?: string;
  purpose?: string;
  writingStyle?: string;
}

interface PageCreationFormProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSimpleCreate: (title: string, pageType?: string) => void;
  onAdvancedCreate: (formData: PageCreationFormData) => void;
  isCreatingSimple?: boolean;
  isGeneratingAdvanced?: boolean;
  generationProgress?: number;
  generationStatus?: 'idle' | 'processing' | 'success' | 'error';
  selectedDocumentType?: string | null;
  onDocumentTypeChange?: (documentType: string | null) => void;
}

const PageCreationForm: React.FC<PageCreationFormProps> = ({
  searchQuery,
  onSearchChange,
  onSimpleCreate,
  onAdvancedCreate,
  isCreatingSimple = false,
  isGeneratingAdvanced = false,
  generationProgress = 0,
  generationStatus = 'idle',
  selectedDocumentType,
  onDocumentTypeChange,
}) => {
  const [isSimpleMode, setIsSimpleMode] = useState(true);
  const [title, setTitle] = useState('');
  const [pageType, setPageType] = useState<PageType | ''>(selectedDocumentType || '');
  const [purpose, setPurpose] = useState('');
  const [writingStyle, setWritingStyle] = useState('');
  
  // Sync pageType with selectedDocumentType when it changes
  useEffect(() => {
    setPageType(selectedDocumentType || '');
  }, [selectedDocumentType]);

  // Handle title change - this serves as both search and creation input
  const handleTitleChange = (value: string) => {
    setTitle(value);
    onSearchChange(value); // Update search filter
  };

  // Handle simple mode creation
  const handleSimpleCreate = () => {
    if (title.trim() && !isCreatingSimple) {
      onSimpleCreate(title.trim(), pageType || undefined);
      // Reset form after successful creation (will be handled by parent)
    }
  };

  // Handle advanced mode creation
  const handleAdvancedCreate = () => {
    if (title.trim() && purpose.trim() && !isGeneratingAdvanced) {
      onAdvancedCreate({
        title: title.trim(),
        pageType: pageType || undefined,
        purpose: purpose.trim(),
        writingStyle: writingStyle || undefined,
      });
      // Reset form after successful creation (will be handled by parent)
    }
  };

  // Form validation - enhanced with visual feedback
  const isSimpleFormValid = title.trim().length >= 3 && !isCreatingSimple;
  const isAdvancedFormValid = title.trim().length >= 3 && purpose.trim().length >= 20 && !isGeneratingAdvanced;
  
  // Word count for purpose field
  const purposeWordCount = purpose.trim().split(/\s+/).filter(word => word.length > 0).length;

  return (
    <div className="space-y-6">
      {/* Document Name Field - Clean label like Image Generator */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-purple-600 dark:text-purple-400">Document Name</h3>
        <StandardTextField
          id="page-name"
          label=""
          value={title}
          onChange={handleTitleChange}
          placeholder="Enter document name"
        />
        
        {/* Enhanced validation feedback */}
        <div className="flex items-center gap-2">
          {title.length > 0 ? (
            title.length >= 3 ? (
              <GlassPill 
                type="Ready to create"
                variant="green"
                className="text-xs py-0.5 px-2"
              />
            ) : (
              <GlassPill 
                type={`${3 - title.length} more characters needed`}
                variant="amber"
                className="text-xs py-0.5 px-2"
              />
            )
          ) : (
            <GlassPill 
              type="3 characters needed"
              variant="gray"
              className="text-xs py-0.5 px-2"
            />
          )}
        </div>
      </div>

      {/* Enhanced Simple/Advanced Toggle */}
      <SimpleAdvancedToggle 
        isSimple={isSimpleMode}
        onToggle={setIsSimpleMode}
      />

      {/* Simple Mode Fields */}
      {isSimpleMode && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          {/* Page Type - Enhanced with StandardSelect */}
          <StandardSelect
            id="page-type-simple"
            label="Document Type (Filter & Create)"
            colorVariant="purple"
            value={pageType || ''}
            onChange={(value) => {
              const newValue = value === '' ? '' : value as PageType;
              setPageType(newValue);
              // Also update the filter
              if (onDocumentTypeChange) {
                onDocumentTypeChange(newValue || null);
              }
            }}
            options={[
              { value: '', label: 'All Documents' },
              ...pageTypeOptions
            ]}
            placeholder="Choose document type..."
          />

          {/* Enhanced Generate Button */}
          <GradientButtonCentered
            onClick={handleSimpleCreate}
            disabled={!isSimpleFormValid}
            variant="purple"
            icon={<Plus size={16} />}
            isLoading={isCreatingSimple}
            fullWidth={true}
            testId="simple-create-button"
          >
            {isCreatingSimple ? 'Creating...' : 'Create Document'}
          </GradientButtonCentered>
        </motion.div>
      )}

      {/* Advanced Mode Fields */}
      {!isSimpleMode && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          {/* Page Type - Enhanced with StandardSelect */}
          <StandardSelect
            id="page-type-advanced"
            label="Document Type (Filter & Create)"
            colorVariant="purple"
            value={pageType || ''}
            onChange={(value) => {
              const newValue = value === '' ? '' : value as PageType;
              setPageType(newValue);
              // Also update the filter
              if (onDocumentTypeChange) {
                onDocumentTypeChange(newValue || null);
              }
            }}
            options={[
              { value: '', label: 'All Documents' },
              ...pageTypeOptions
            ]}
            placeholder="Choose document type..."
          />

          {/* Writing Style - No label, positioned directly after Page Type */}
          <WritingStyleDropdown
            selectedAssistantId={writingStyle}
            onAssistantChange={setWritingStyle}
          />

          {/* Enhanced Purpose Field */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-purple-600 dark:text-purple-400">Document Description</h4>
            <StandardTextAreaField
              id="purpose"
              label=""
              value={purpose}
              onChange={setPurpose}
              placeholder="Describe the purpose of this page in detail..."
              rows={4}
              className="resize-none"
            />
            
            {/* Enhanced validation feedback */}
            <div className="flex items-center gap-2">
              {purpose.length < 20 && (
                <GlassPill 
                  type="Min 20 characters"
                  variant="amber"
                  className="text-xs py-0.5 px-2"
                />
              )}
              <GlassPill 
                type={`${purpose.length} / 1000 characters`}
                variant={purpose.length >= 20 ? "purple" : "gray"}
                className="text-xs py-0.5 px-2"
              />
              {purposeWordCount > 0 && (
                <GlassPill 
                  type={`${purposeWordCount} words`}
                  variant={purpose.length >= 20 ? "green" : "gray"}
                  className="text-xs py-0.5 px-2"
                />
              )}
            </div>
          </div>

          {/* Enhanced Generate Button with Progress */}
          <GradientButtonCentered
            onClick={handleAdvancedCreate}
            disabled={!isAdvancedFormValid}
            variant="purple"
            icon={!isGeneratingAdvanced ? <Plus size={16} /> : undefined}
            isLoading={isGeneratingAdvanced}
            fullWidth={true}
            testId="advanced-create-button"
          >
            {isGeneratingAdvanced ? (
              <div className="flex flex-col items-center w-full">
                <span className="mb-1">
                  {generationStatus === 'processing' ? 'Generating Content...' : 'Starting...'}
                </span>
                {generationProgress > 0 && (
                  <div className="w-full bg-white/20 rounded-full h-1">
                    <div 
                      className="bg-white h-1 rounded-full transition-all duration-500" 
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                )}
              </div>
            ) : (
              'Generate Content'
            )}
          </GradientButtonCentered>
        </motion.div>
      )}
    </div>
  );
};

export default PageCreationForm; 